import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  Copy, 
  Check, 
  Key, 
  Shield, 
  Zap, 
  FileText,
  AlertCircle,
  CheckCircle2,
  Code2,
  Terminal,
  BookOpen
} from "lucide-react";
import { toast } from "sonner";

const ApiDocs = () => {
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  const copyToClipboard = (text: string, snippetId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippet(snippetId);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const baseUrl = "https://tnhjxxnbzlxluwbnqxky.supabase.co/functions/v1";

  const curlExample = `curl -X POST "${baseUrl}/analyze-report" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: your_api_key_here" \\
  -d '{
    "reportText": "Your credit report text here...",
    "clientReference": "client-123"
  }'`;

  const jsExample = `const response = await fetch(
  "${baseUrl}/analyze-report",
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "your_api_key_here"
    },
    body: JSON.stringify({
      reportText: "Your credit report text here...",
      clientReference: "client-123"
    })
  }
);

const result = await response.json();
console.log(result);`;

  const pythonExample = `import requests

response = requests.post(
    "${baseUrl}/analyze-report",
    headers={
        "Content-Type": "application/json",
        "x-api-key": "your_api_key_here"
    },
    json={
        "reportText": "Your credit report text here...",
        "clientReference": "client-123"
    }
)

result = response.json()
print(result)`;

  const responseExample = `{
  "success": true,
  "scanId": "550e8400-e29b-41d4-a716-446655440000",
  "analysis": {
    "violations": [
      {
        "type": "INACCURATE_INFORMATION",
        "severity": "high",
        "description": "Account balance reported incorrectly",
        "fcraSection": "Section 611",
        "recommendation": "File a dispute with the credit bureau"
      }
    ],
    "summary": {
      "totalViolations": 3,
      "highSeverity": 1,
      "mediumSeverity": 1,
      "lowSeverity": 1
    },
    "recommendations": [
      "Dispute inaccurate account information",
      "Request validation of debt from collector"
    ]
  }
}`;

  const errorExample = `{
  "success": false,
  "error": "Invalid API key",
  "code": "UNAUTHORIZED"
}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="font-semibold">API Documentation</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/auth">
              <Button variant="outline" size="sm">Log In</Button>
            </Link>
            <Link to="/auth">
              <Button size="sm">Get API Key</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Quick Navigation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <a href="#getting-started" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Getting Started
                  </a>
                  <a href="#authentication" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Authentication
                  </a>
                  <a href="#endpoints" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Endpoints
                  </a>
                  <a href="#analyze-report" className="block text-sm text-muted-foreground hover:text-primary transition-colors pl-4">
                    → Analyze Report
                  </a>
                  <a href="#responses" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Response Formats
                  </a>
                  <a href="#errors" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Error Handling
                  </a>
                  <a href="#rate-limits" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Rate Limits
                  </a>
                </CardContent>
              </Card>

              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Need Help?</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contact our support team for integration assistance.
                      </p>
                      <Button size="sm" variant="link" className="px-0 mt-2 h-auto">
                        support@creditreportmoney.com
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-12">
            {/* Hero */}
            <section>
              <Badge variant="secondary" className="mb-4">v1.0</Badge>
              <h1 className="text-4xl font-bold tracking-tight mb-4">
                Credit Analysis API
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Integrate AI-powered credit report analysis into your platform with our simple REST API. 
                Detect FCRA violations, generate insights, and deliver value to your clients.
              </p>
            </section>

            {/* Getting Started */}
            <section id="getting-started" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Terminal className="h-6 w-6 text-primary" />
                Getting Started
              </h2>
              <Card>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        1
                      </div>
                      <div>
                        <h3 className="font-medium">Create Account</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Sign up for a free account to get started
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        2
                      </div>
                      <div>
                        <h3 className="font-medium">Generate API Key</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Create an API key from your dashboard
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        3
                      </div>
                      <div>
                        <h3 className="font-medium">Start Integrating</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use the examples below to make your first call
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Authentication */}
            <section id="authentication" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Key className="h-6 w-6 text-primary" />
                Authentication
              </h2>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">API Key Authentication</CardTitle>
                  <CardDescription>
                    All API requests require authentication using an API key passed in the request headers.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Header Format</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard('x-api-key: your_api_key_here', 'header')}
                      >
                        {copiedSnippet === 'header' ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <code className="text-sm text-primary">x-api-key: your_api_key_here</code>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Keep Your API Key Secure</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Never expose your API key in client-side code. Always make API calls from your backend server.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Endpoints */}
            <section id="endpoints" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Code2 className="h-6 w-6 text-primary" />
                Endpoints
              </h2>

              {/* Analyze Report Endpoint */}
              <Card id="analyze-report" className="scroll-mt-24">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-green-500 hover:bg-green-600">POST</Badge>
                    <code className="text-lg font-mono">/analyze-report</code>
                  </div>
                  <CardDescription className="mt-3">
                    Analyze a credit report for FCRA violations and generate actionable insights.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Request Parameters */}
                  <div>
                    <h4 className="font-medium mb-3">Request Body</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-3 font-medium">Parameter</th>
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Required</th>
                            <th className="text-left p-3 font-medium">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          <tr>
                            <td className="p-3"><code className="text-primary">reportText</code></td>
                            <td className="p-3">string</td>
                            <td className="p-3"><Badge variant="destructive" className="text-xs">Required</Badge></td>
                            <td className="p-3 text-muted-foreground">The full text content of the credit report</td>
                          </tr>
                          <tr>
                            <td className="p-3"><code className="text-primary">clientReference</code></td>
                            <td className="p-3">string</td>
                            <td className="p-3"><Badge variant="secondary" className="text-xs">Optional</Badge></td>
                            <td className="p-3 text-muted-foreground">Your internal reference ID for tracking</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Code Examples */}
                  <div>
                    <h4 className="font-medium mb-3">Code Examples</h4>
                    <Tabs defaultValue="curl" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="curl">cURL</TabsTrigger>
                        <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                        <TabsTrigger value="python">Python</TabsTrigger>
                      </TabsList>
                      <TabsContent value="curl" className="mt-4">
                        <div className="relative">
                          <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                            <code>{curlExample}</code>
                          </pre>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(curlExample, 'curl')}
                          >
                            {copiedSnippet === 'curl' ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TabsContent>
                      <TabsContent value="javascript" className="mt-4">
                        <div className="relative">
                          <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                            <code>{jsExample}</code>
                          </pre>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(jsExample, 'js')}
                          >
                            {copiedSnippet === 'js' ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TabsContent>
                      <TabsContent value="python" className="mt-4">
                        <div className="relative">
                          <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                            <code>{pythonExample}</code>
                          </pre>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(pythonExample, 'python')}
                          >
                            {copiedSnippet === 'python' ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Response Formats */}
            <section id="responses" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <FileText className="h-6 w-6 text-primary" />
                Response Formats
              </h2>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Success Response
                  </CardTitle>
                  <CardDescription>
                    HTTP Status: 200 OK
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                      <code>{responseExample}</code>
                    </pre>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(responseExample, 'response')}
                    >
                      {copiedSnippet === 'response' ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  <Separator className="my-6" />

                  <h4 className="font-medium mb-3">Response Fields</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3 font-medium">Field</th>
                          <th className="text-left p-3 font-medium">Type</th>
                          <th className="text-left p-3 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr>
                          <td className="p-3"><code className="text-primary">success</code></td>
                          <td className="p-3">boolean</td>
                          <td className="p-3 text-muted-foreground">Indicates if the request was successful</td>
                        </tr>
                        <tr>
                          <td className="p-3"><code className="text-primary">scanId</code></td>
                          <td className="p-3">string</td>
                          <td className="p-3 text-muted-foreground">Unique identifier for this scan</td>
                        </tr>
                        <tr>
                          <td className="p-3"><code className="text-primary">analysis.violations</code></td>
                          <td className="p-3">array</td>
                          <td className="p-3 text-muted-foreground">List of detected FCRA violations</td>
                        </tr>
                        <tr>
                          <td className="p-3"><code className="text-primary">analysis.summary</code></td>
                          <td className="p-3">object</td>
                          <td className="p-3 text-muted-foreground">Summary statistics of the analysis</td>
                        </tr>
                        <tr>
                          <td className="p-3"><code className="text-primary">analysis.recommendations</code></td>
                          <td className="p-3">array</td>
                          <td className="p-3 text-muted-foreground">Actionable recommendations for the client</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Error Handling */}
            <section id="errors" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-primary" />
                Error Handling
              </h2>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Error Response Format</CardTitle>
                  <CardDescription>
                    When an error occurs, the API returns a JSON response with error details.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="relative">
                    <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                      <code>{errorExample}</code>
                    </pre>
                  </div>

                  <h4 className="font-medium mb-3">Error Codes</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3 font-medium">HTTP Status</th>
                          <th className="text-left p-3 font-medium">Code</th>
                          <th className="text-left p-3 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr>
                          <td className="p-3"><Badge variant="destructive">400</Badge></td>
                          <td className="p-3"><code className="text-primary">BAD_REQUEST</code></td>
                          <td className="p-3 text-muted-foreground">Invalid request body or missing required fields</td>
                        </tr>
                        <tr>
                          <td className="p-3"><Badge variant="destructive">401</Badge></td>
                          <td className="p-3"><code className="text-primary">UNAUTHORIZED</code></td>
                          <td className="p-3 text-muted-foreground">Invalid or missing API key</td>
                        </tr>
                        <tr>
                          <td className="p-3"><Badge variant="destructive">403</Badge></td>
                          <td className="p-3"><code className="text-primary">FORBIDDEN</code></td>
                          <td className="p-3 text-muted-foreground">API key doesn't have access or domain not whitelisted</td>
                        </tr>
                        <tr>
                          <td className="p-3"><Badge variant="destructive">429</Badge></td>
                          <td className="p-3"><code className="text-primary">RATE_LIMITED</code></td>
                          <td className="p-3 text-muted-foreground">Too many requests, please slow down</td>
                        </tr>
                        <tr>
                          <td className="p-3"><Badge variant="destructive">402</Badge></td>
                          <td className="p-3"><code className="text-primary">INSUFFICIENT_CREDITS</code></td>
                          <td className="p-3 text-muted-foreground">Organization has no remaining scan credits</td>
                        </tr>
                        <tr>
                          <td className="p-3"><Badge variant="destructive">500</Badge></td>
                          <td className="p-3"><code className="text-primary">INTERNAL_ERROR</code></td>
                          <td className="p-3 text-muted-foreground">Server error, please try again later</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Rate Limits */}
            <section id="rate-limits" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Rate Limits
              </h2>
              <Card>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium mb-2">Free Tier</h4>
                      <p className="text-2xl font-bold text-primary">10</p>
                      <p className="text-sm text-muted-foreground">requests per minute</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium mb-2">Starter Tier</h4>
                      <p className="text-2xl font-bold text-primary">60</p>
                      <p className="text-sm text-muted-foreground">requests per minute</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium mb-2">Professional Tier</h4>
                      <p className="text-2xl font-bold text-primary">300</p>
                      <p className="text-sm text-muted-foreground">requests per minute</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium mb-2">Enterprise Tier</h4>
                      <p className="text-2xl font-bold text-primary">Custom</p>
                      <p className="text-sm text-muted-foreground">Contact sales for higher limits</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mt-6">
                    <Zap className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Rate Limit Headers</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Check <code className="text-primary">X-RateLimit-Remaining</code> and{" "}
                        <code className="text-primary">X-RateLimit-Reset</code> headers in the response to monitor your usage.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* CTA */}
            <section className="pt-8">
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <h3 className="text-xl font-semibold mb-2">Ready to Get Started?</h3>
                    <p className="text-muted-foreground mb-6">
                      Create your account and get your API key in minutes.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <Link to="/auth">
                        <Button size="lg">
                          Create Free Account
                        </Button>
                      </Link>
                      <Link to="/#pricing">
                        <Button variant="outline" size="lg">
                          View Pricing
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;
